import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { loginByUnionId, loginMethod } from '@/utils/login';

import Layout from '@/layout/index.vue';
import { useAppStore } from '@/store/appStore';
import { softwareRouter } from '@/router/module/software';
import { androidScreenRoute } from '@/router/module/android-screen';
import { reactNativeRoute } from '@/router/module/reactnative';
import { firmwareRoute } from '@/router/module/firmware';
import { adminRoute } from '@/router/module/admin';
import { publicRoute } from '@/router/module/public';
import { forkliftFirmwareRoute } from '@/router/module/forklift-firmware';
import { Role } from '@/router/enum';
import { LoginSessionKey } from '@/enum/app';
import { androidAppRoute } from '@/router/module/android-app.ts';
import { intersection } from 'lodash';

declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    key: string;
    icon?: string;
    permission: Role[];
    inside?: boolean;
    /** IAM p_id 列表（OR 语义），声明后菜单与路由均按其隐藏 */
    iamPermission?: string[];
  }
}

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: Layout,
    name: 'Home',
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'home',
        component: () => import('@/views/back-stage-management/Home/index.vue'),
        meta: {
          title: '首页',
          key: 'home',
          icon: 'HomeOutlined',
          permission: [Role.ADMIN, Role.USER, Role.LINUX_ADMIN],
        },
      },

      // 固件
      firmwareRoute,

      // 叉车固件
      forkliftFirmwareRoute,

      // 上位机
      softwareRouter,

      // 安卓应用
      androidAppRoute,

      // 安卓屏幕
      androidScreenRoute,

      // ReactNative
      reactNativeRoute,

      // 管理员
      ...adminRoute,
    ],
  },

  // 登录页
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
  },

  // 公开路由
  ...publicRoute,

  // 兜底
  {
    path: '/:w+',
    name: '*',
    component: () => import('@/views/login/index.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/** 获取当前用户拥有的角色 */
const getUserRoles = (): Role[] => {
  const appStore = useAppStore();

  // forklift 类型用户只能访问 forklift 专属路由
  if (appStore.userType === 'forklift') {
    return [Role.FORKLIFT_USER];
  }

  const roles: Role[] = [Role.USER];

  if (appStore.isAdmin) {
    roles.push(Role.ADMIN);
  }
  if (appStore.permission.includes(Role.LINUX_ADMIN)) {
    roles.push(Role.LINUX_ADMIN);
  }
  if (appStore.permission.includes(Role.ANDROID_ADMIN)) {
    roles.push(Role.ANDROID_ADMIN);
  }
  if (appStore.permission.includes(Role.FORKLIFT_ADMIN)) {
    roles.push(Role.FORKLIFT_ADMIN);
  }

  return roles;
};

/**前置路由守卫 */
router.beforeEach(async (to, _, next) => {
  /**获取store中token数据 */
  const token = useAppStore().token;

  /**获取存储在session中的登录信息 */
  const loginParam = sessionStorage.getItem(LoginSessionKey.LOGIN_PARAM);

  const union_id = sessionStorage.getItem(LoginSessionKey.UNION_ID);

  /**根据token判断用户是否登录 */

  /** 已登陆 */
  if (token !== '') {
    const appStore = useAppStore();

    if (to.path === '/login') {
      next({ path: '/' });
      return;
    }

    // forklift 类型用户只能访问固件总览和固件列表
    if (appStore.userType === 'forklift') {
      const forkliftAllowedNames = [
        'forklift-publish-overview',
        'forklift-publish',
        'forklift-map-code-detail',
        'forklift-map-code-import',
        'forklift-publish-detail',
      ];
      if (!forkliftAllowedNames.includes(to.name as string)) {
        next({ name: 'forklift-publish-overview' });
        return;
      }
      next();
      return;
    }

    // 校验路由权限（公开路由跳过）
    if (to.meta.permission && !to.meta.permission.includes(Role.FREE)) {
      const userRoles = getUserRoles();
      if (intersection(to.meta.permission, userRoles).length === 0) {
        next({ path: '/home' });
        return;
      }
    }

    // 路由声明 meta.iamPermission（IAM p_id，OR 语义）时校验，无权限回首页
    if (to.meta.iamPermission?.length) {
      const appStore = useAppStore();
      if (!appStore.fkPermission(to.meta.iamPermission)) {
        next({ path: '/home' });
        return;
      }
    }

    next();
  } else {
    /** 未登录 */
    // 登录页
    if (['/login'].indexOf(to.path) !== -1) {
      next();
    }
    // 如果有缓存的登录信息
    else if (loginParam) {
      await loginMethod(JSON.parse(loginParam), true);
      next();
    }
    // 基于 union id 登录
    else if (union_id) {
      await loginByUnionId(union_id, true);
      next();
    }

    // 白名单直接进入
    else if (to.meta.permission.includes(Role.FREE)) {
      next();
    } else {
      /**其他页面需跳转至登录页 */
      next(`/login?redirect=${to.path}`);
    }
  }
});

/**路由后置守卫 */
router.afterEach((to) => {
  document.title = to.meta.title ? to.meta.title : '士腾科技 · OTA系统';
});

export default router;
